/**
 * The shared colour anchor palette.
 *
 * MACHINE GENERATED by `scripts/colour_anchors.py` - do not edit by hand.
 *
 * Both the Garaimon art profiles and the colours a player gathers are
 * described as proportions over these anchors, which is what makes the two
 * comparable. Colours closer together than the merge threshold were merged,
 * so minor shading differences cannot become separate categories.
 *
 * `oklab` is the anchor's position in Oklab, used for perceptual distance.
 * Merge threshold: 0.06 (Oklab distance).
 */

export interface ColourAnchor {
  id: string;
  hex: string;
  oklab: [number, number, number];
  /** How many source colours were merged into this anchor. */
  members: number;
}

export const COLOUR_ANCHOR_THRESHOLD = 0.06;

export const COLOUR_ANCHORS: readonly ColourAnchor[] = [
  { id: "a00", hex: "#11120e", oklab: [0.1809, -0.0049, 0.0068], members: 2 },
  { id: "a01", hex: "#0c270f", oklab: [0.2443, -0.0456, 0.0310], members: 1 },
  { id: "a02", hex: "#2f241e", oklab: [0.2712, 0.0128, 0.0154], members: 1 },
  { id: "a03", hex: "#092852", oklab: [0.2815, -0.0185, -0.0828], members: 1 },
  { id: "a04", hex: "#193c1f", oklab: [0.3213, -0.0550, 0.0349], members: 1 },
  { id: "a05", hex: "#193b6b", oklab: [0.3546, -0.0200, -0.0903], members: 1 },
  { id: "a06", hex: "#573226", oklab: [0.3601, 0.0451, 0.0361], members: 1 },
  { id: "a07", hex: "#255a31", oklab: [0.4201, -0.0752, 0.0456], members: 1 },
  { id: "a08", hex: "#6e422e", oklab: [0.4263, 0.0488, 0.0486], members: 1 },
  { id: "a09", hex: "#24508c", oklab: [0.4327, -0.0250, -0.1078], members: 1 },
  { id: "a10", hex: "#555754", oklab: [0.4540, -0.0038, 0.0038], members: 1 },
  { id: "a11", hex: "#366e3e", oklab: [0.4869, -0.0807, 0.0524], members: 1 },
  { id: "a12", hex: "#8e5a39", oklab: [0.5177, 0.0511, 0.0654], members: 2 },
  { id: "a13", hex: "#2e67b9", oklab: [0.5195, -0.0298, -0.1394], members: 1 },
  { id: "a14", hex: "#756b54", oklab: [0.5306, 0.0017, 0.0364], members: 1 },
  { id: "a15", hex: "#54834b", oklab: [0.5609, -0.0745, 0.0614], members: 2 },
  { id: "a16", hex: "#2072f8", oklab: [0.5849, -0.0363, -0.2119], members: 1 },
  { id: "a17", hex: "#7b837e", oklab: [0.6017, -0.0112, 0.0044], members: 1 },
  { id: "a18", hex: "#3a83d4", oklab: [0.6032, -0.0413, -0.1373], members: 1 },
  { id: "a19", hex: "#689759", oklab: [0.6257, -0.0767, 0.0678], members: 2 },
  { id: "a20", hex: "#b27e51", oklab: [0.6353, 0.0431, 0.0771], members: 2 },
  { id: "a21", hex: "#3094f8", oklab: [0.6602, -0.0529, -0.1655], members: 2 },
  { id: "a22", hex: "#9c80d9", oklab: [0.6622, 0.0600, -0.1169], members: 1 },
  { id: "a23", hex: "#db7767", oklab: [0.6779, 0.1104, 0.0644], members: 1 },
  { id: "a24", hex: "#71ae61", oklab: [0.6904, -0.0949, 0.0815], members: 1 },
  { id: "a25", hex: "#54aebb", oklab: [0.7008, -0.0774, -0.0410], members: 1 },
  { id: "a26", hex: "#7e9de5", oklab: [0.7020, -0.0081, -0.1115], members: 1 },
  { id: "a27", hex: "#d77bc2", oklab: [0.7059, 0.1314, -0.0584], members: 1 },
  { id: "a28", hex: "#a2a298", oklab: [0.7093, -0.0039, 0.0130], members: 2 },
  { id: "a29", hex: "#cf9f6b", oklab: [0.7356, 0.0335, 0.0825], members: 2 },
  { id: "a30", hex: "#ee8c5e", oklab: [0.7358, 0.0942, 0.0951], members: 3 },
  { id: "a31", hex: "#56c48b", oklab: [0.7410, -0.1222, 0.0491], members: 1 },
  { id: "a32", hex: "#7fbdf6", oklab: [0.7782, -0.0402, -0.0959], members: 1 },
  { id: "a33", hex: "#70c8cc", oklab: [0.7794, -0.0802, -0.0282], members: 1 },
  { id: "a34", hex: "#efaa57", oklab: [0.7893, 0.0458, 0.1202], members: 3 },
  { id: "a35", hex: "#bfbfb7", oklab: [0.8024, -0.0032, 0.0106], members: 1 },
  { id: "a36", hex: "#c4b4f0", oklab: [0.8040, 0.0382, -0.0761], members: 1 },
  { id: "a37", hex: "#f0a8c3", oklab: [0.8079, 0.0906, -0.0077], members: 1 },
  { id: "a38", hex: "#8cd5b5", oklab: [0.8160, -0.0834, 0.0223], members: 1 },
  { id: "a39", hex: "#b6d85e", oklab: [0.8335, -0.0838, 0.1294], members: 2 },
  { id: "a40", hex: "#edd077", oklab: [0.8627, -0.0034, 0.1135], members: 3 },
  { id: "a41", hex: "#b2dbfa", oklab: [0.8725, -0.0295, -0.0533], members: 1 },
  { id: "a42", hex: "#d5eb9c", oklab: [0.9065, -0.0543, 0.0900], members: 2 },
  { id: "a43", hex: "#ebe1c0", oklab: [0.9094, -0.0034, 0.0455], members: 3 },
];
